import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { Interaction, InteractionListResponse } from '@/lib/types'

export function useInteractions(contactId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['interactions', contactId, page],
    queryFn: () =>
      api<InteractionListResponse>(`/api/contacts/${contactId}/interactions?page=${page}`),
    enabled: !!contactId,
  })
}

export function useCreateInteraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api<Interaction>('/api/interactions', { method: 'POST', body: JSON.stringify(data) }),
    onMutate: async (variables) => {
      // Support both single contact_id and contact_ids array
      const contactIds =
        (variables.contact_ids as string[]) ??
        (variables.contact_id ? [variables.contact_id as string] : [])
      const primaryContactId = contactIds[0] ?? ''

      await qc.cancelQueries({ queryKey: ['interactions', primaryContactId] })

      const interactionQueries = qc.getQueriesData<InteractionListResponse>({
        queryKey: ['interactions', primaryContactId],
      })
      const previousData = new Map(interactionQueries)

      const optimisticInteraction: Interaction = {
        id: `optimistic-${Date.now()}`,
        userId: '',
        contactId: primaryContactId,
        type: (variables.type as string) || 'other',
        title: (variables.title as string) || null,
        notes: (variables.notes as string) || null,
        location: (variables.location as string) || null,
        meetingContext: (variables.meeting_context as string) || null,
        quality: (variables.quality as string) || null,
        occurredAt: (variables.occurred_at as string) || new Date().toISOString(),
        createdAt: new Date().toISOString(),
        participants: [],
      }

      qc.setQueriesData<InteractionListResponse>(
        { queryKey: ['interactions', primaryContactId] },
        (old) => {
          if (!old) return old
          return { ...old, items: [optimisticInteraction, ...old.items], total: old.total + 1 }
        },
      )

      return { previousData, contactIds }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          qc.setQueryData(key, data)
        }
      }
      toast.error(i18n.t('toast.error'))
    },
    onSuccess: () => {
      toast.success(i18n.t('toast.interactionLogged'))
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate interaction and timeline queries for ALL linked contacts
      const contactIds =
        (variables.contact_ids as string[]) ??
        (variables.contact_id ? [variables.contact_id as string] : [])
      for (const cId of contactIds) {
        qc.invalidateQueries({ queryKey: ['interactions', cId] })
        qc.invalidateQueries({ queryKey: ['timeline', cId] })
      }
    },
  })
}

export function useUpdateInteraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api<Interaction>(`/api/interactions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions'] })
      qc.invalidateQueries({ queryKey: ['timeline'] })
      toast.success(i18n.t('toast.interactionUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteInteraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/interactions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions'] })
      qc.invalidateQueries({ queryKey: ['timeline'] })
      toast.success(i18n.t('toast.interactionDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
